import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { queryOptions, useSuspenseQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useServerFn } from '@tanstack/react-start';
import { SiteLayout } from '@/components/SiteLayout';
import { Container } from '@/components/Container';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { listActiveDeliveryAreas } from '@/lib/cms/delivery-areas.functions';
import { getTiffinCatalog, placeTiffinOrder } from '@/lib/tiffin/tiffin.functions';
import { validateCoupon } from '@/lib/public/mp2-coupons.functions';
import { calculateQuote, dayNames, durationOptions, type Catalog, type QuoteInput } from '@/lib/tiffin/rules';
import { formatPaise } from '@/lib/format/money';
import { canonicalUrl } from '@/lib/seo/site';
import { tiffinSeoTitle as title, tiffinSeoDescription as description, tiffinServiceAreas, tiffinStructuredData } from '@/lib/tiffin/seo';
import { useCustomerSession } from '@/hooks/useCustomerSession';
import { useCustomerAddresses } from '@/hooks/useCustomerAccount';
import type { CustomerAddress } from '@/lib/customer/customer-api';

const catalogQuery = queryOptions({ queryKey: ['tiffin', 'public'], queryFn: () => getTiffinCatalog() });
const areasQuery = queryOptions({ queryKey: ['delivery-areas', 'public'], queryFn: () => listActiveDeliveryAreas() });
const DRAFT_KEY = 'tiffin_draft';

export const Route = createFileRoute('/tiffin')({
  loader: async ({ context }) => {
    const [catalog, areas] = await Promise.all([context.queryClient.ensureQueryData(catalogQuery), context.queryClient.ensureQueryData(areasQuery)]);
    return { structuredData: tiffinStructuredData({ ...catalog, areas: areas ?? [] }), image: catalog.settings?.promotional_image };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title }, { name: 'description', content: description },
      { property: 'og:title', content: title }, { property: 'og:description', content: description }, { property: 'og:url', content: canonicalUrl('/tiffin') },
      { property: 'og:type', content: 'website' }, { name: 'twitter:card', content: 'summary' },
      ...(loaderData?.image?.startsWith('https://') ? [
        { property: 'og:image', content: loaderData.image },
        { name: 'twitter:image', content: loaderData.image },
      ] : []),
    ],
    links: [{ rel: 'canonical', href: canonicalUrl('/tiffin') }],
    scripts: loaderData?.structuredData ? [{ type: 'application/ld+json', children: JSON.stringify(loaderData.structuredData) }] : [],
  }),
  component: TiffinPage,
  errorComponent: () => (
    <SiteLayout><Container className="py-12"><h1 className="font-display text-3xl">Tiffin Subscription</h1>
      <p className="mt-4" role="alert">Tiffin details could not be loaded. Please try again later.</p></Container></SiteLayout>
  ),
});

function TiffinPage() {
  const { data } = useSuspenseQuery(catalogQuery);
  const s = data.settings;
  return (
    <SiteLayout>
      <Container className="py-10 md:py-14 space-y-8 max-w-5xl">
        <header className="space-y-3">
          <h1 className="font-display text-3xl md:text-4xl">Tiffin Subscription in Mumbai</h1>
          <p className="text-primary">Vegetarian · Lunch & dinner</p>
          {s?.description && <p className="whitespace-pre-wrap">{s.description}</p>}
        </header>
        {s?.promotional_image && <img src={s.promotional_image} alt="Poonam Kapur Healthy Kitchen vegetarian tiffin subscription in Mumbai" className="w-full max-h-96 rounded-md object-contain bg-muted" />}
        {s?.subscriptions_open ? <TiffinCheckout catalog={data} /> : <p role="status" className="border-l-4 border-primary bg-muted p-4">New tiffin subscriptions are temporarily unavailable. Please check back soon.</p>}
        {s && <TiffinInfo catalog={data} />}
        <Link to="/account/tiffin" className="inline-block text-primary underline text-sm">My tiffin orders and subscriptions</Link>
      </Container>
    </SiteLayout>
  );
}

const selectClass = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';
type Details = { name: string; phone: string; email: string; address: string; line2: string; city: string; pincode: string; notes: string };
const emptyDetails: Details = { name: '', phone: '', email: '', address: '', line2: '', city: 'Mumbai', pincode: '', notes: '' };

function TiffinCheckout({ catalog }: { catalog: Catalog }) {
  const s = catalog.settings;
  if (!s) return null;
  const session = useCustomerSession();
  const navigate = useNavigate();
  const { data: liveAreas } = useSuspenseQuery(areasQuery);
  // Same active delivery-area list as Meal Plans, narrowed only if admin chose specific areas.
  const areas = useMemo(() => (liveAreas ?? []).filter((a) => !s.delivery_area_ids.length || s.delivery_area_ids.includes(a.id)), [liveAreas, s.delivery_area_ids]);
  const options = durationOptions(s).filter((o) => o.enabled);
  const [selection, setSelection] = useState<QuoteInput['selection']>('lunch');
  const [duration, setDuration] = useState(options[0]?.id ?? '');
  const [start, setStart] = useState('');
  const [area, setArea] = useState('');
  const [details, setDetails] = useState<Details>(emptyDetails);
  const [payment, setPayment] = useState<'razorpay' | 'cod'>('razorpay');
  const [codAck, setCodAck] = useState(false);
  const [coupon, setCoupon] = useState('');
  const [couponState, setCouponState] = useState<{ code: string; discount: number } | { error: string } | null>(null);
  const [requestKey] = useState(() => crypto.randomUUID());

  // Restore selections saved before sign-in (no payment data is stored).
  useEffect(() => {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return;
    sessionStorage.removeItem(DRAFT_KEY);
    try {
      const d = JSON.parse(raw);
      if (['lunch', 'dinner', 'both'].includes(d.selection)) setSelection(d.selection);
      if (typeof d.duration === 'string') setDuration(d.duration);
      if (typeof d.start === 'string') setStart(d.start);
      if (typeof d.area === 'string') setArea(d.area);
    } catch { /* ignore */ }
  }, []);

  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const tomorrow = new Date(`${today}T00:00:00Z`); tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const quote = start && area ? calculateQuote({ ...catalog, areas }, { selection, duration, start, area }, today) : null;
  const option = options.find((o) => o.id === duration);
  const slots = selection === 'both' ? 2 : 1;
  const perDay = (selection !== 'dinner' ? s.lunch_price_paise ?? 0 : 0) + (selection !== 'lunch' ? s.dinner_price_paise ?? 0 : 0);
  const days = option?.delivery_days ?? 0;
  const food = perDay * days;
  const delivery = s.delivery_policy === 'included' ? 0 : (s.delivery_price_paise ?? 0) * slots * days;
  const discount = couponState && 'discount' in couponState ? couponState.discount : 0;

  const checkCoupon = useServerFn(validateCoupon);
  const couponMutation = useMutation({
    mutationFn: () => checkCoupon({ data: { code: coupon, total_paise: food, customer_id: session.user?.id } }),
    onSuccess: (r: any) => {
      if (!r?.ok) return setCouponState({ error: r?.error === 'expired' ? 'This coupon has expired.' : r?.error === 'min_order_not_met' ? 'Your order does not meet this coupon\'s minimum amount.' : r?.error?.includes('limit') ? 'This coupon has reached its usage limit.' : 'This coupon is not valid.' });
      if (!r.stack_with_subscription) return setCouponState({ error: 'This coupon cannot be used for tiffin subscriptions.' });
      // Display estimate using the same rule as the database; the order recalculates it.
      const raw = r.type === 'percentage' ? Math.floor((food * Number(r.value ?? 0)) / 100) : Math.floor(Number(r.value ?? 0));
      setCouponState({ code: coupon.trim().toUpperCase(), discount: Math.max(0, Math.min(food, raw, r.max_discount_paise ?? Infinity)) });
    },
    onError: () => setCouponState({ error: 'This coupon could not be checked. Please try again.' }),
  });
  useEffect(() => { setCouponState(null); }, [selection, duration]);

  const place = useServerFn(placeTiffinOrder);
  const order = useMutation({
    mutationFn: () => place({ data: {
      selection, duration, start, area, payment_method: payment, request_key: requestKey,
      coupon_code: couponState && 'code' in couponState ? couponState.code : undefined,
      customer: { ...details, cod_acknowledgement: payment === 'cod' && codAck },
    } }),
    onSuccess: (r) => { if (r.ok && r.paymentToken) void navigate({ to: '/order/pay', search: { token: r.paymentToken } }); },
  });

  const useAddress = (a: CustomerAddress) => {
    setDetails((d) => ({ ...d, name: a.contact_name ?? d.name, phone: a.contact_phone ?? d.phone, address: a.address_line1, line2: a.address_line2 ?? '', city: a.city || d.city, pincode: a.pincode ?? '', notes: a.delivery_notes ?? '' }));
    if (a.delivery_area_id && areas.some((x) => x.id === a.delivery_area_id)) setArea(a.delivery_area_id);
  };
  const signIn = () => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ selection, duration, start, area }));
    void navigate({ to: '/account/sign-in', search: { redirect: '/tiffin' } as never });
  };

  if (order.data?.ok && !order.data.paymentToken) {
    return (
      <section role="status" className="space-y-3 rounded-md border border-border p-6">
        <h2 className="font-display text-2xl">Order received</h2>
        <p>Reference <strong>{order.data.orderRef}</strong> · {formatPaise(order.data.totalPaise)} to pay in cash on delivery.</p>
        <p className="text-sm text-muted-foreground">Payment status: unpaid until collected. Your subscription becomes active once payment is confirmed.</p>
        <Link to="/account/tiffin" className="text-primary underline">View in My Account</Link>
      </section>
    );
  }

  const canSubmit = !!quote?.ok && !!session.user && !order.isPending && (payment !== 'cod' || codAck);
  return (
    <form className="space-y-7" onSubmit={(e) => { e.preventDefault(); if (canSubmit) order.mutate(); }}>
      <section className="space-y-4">
        <h2 className="font-display text-2xl">Choose your tiffin</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label htmlFor="tiffin-meals">Daily meals</Label>
            <select id="tiffin-meals" className={selectClass} value={selection} onChange={(e) => setSelection(e.target.value as QuoteInput['selection'])}>
              <option value="lunch">Lunch only</option><option value="dinner">Dinner only</option><option value="both">Lunch + Dinner</option>
            </select></div>
          <div className="space-y-2"><Label htmlFor="tiffin-duration">Duration</Label>
            <select id="tiffin-duration" className={selectClass} value={duration} onChange={(e) => setDuration(e.target.value)} required>
              {options.map((o) => <option key={o.id} value={o.id}>{o.label} · {o.delivery_days} delivery {o.delivery_days === 1 ? 'day' : 'days'}</option>)}
            </select></div>
          <div className="space-y-2"><Label htmlFor="tiffin-start">Start date</Label>
            <Input id="tiffin-start" type="date" min={tomorrow.toISOString().slice(0, 10)} required value={start} onChange={(e) => setStart(e.currentTarget.value)} /></div>
          <div className="space-y-2"><Label htmlFor="tiffin-area">Delivery area</Label>
            <select id="tiffin-area" className={selectClass} value={area} required onChange={(e) => setArea(e.target.value)}>
              <option value="">Select area</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.neighbourhood}</option>)}
            </select></div>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-sm max-w-lg">
          <dt>Food ({formatPaise(perDay)} × {days} {days === 1 ? 'day' : 'days'})</dt><dd className="text-right">{formatPaise(food)}</dd>
          <dt>Delivery {s.delivery_policy === 'included' ? '(included)' : `(${formatPaise(s.delivery_price_paise)} × ${slots} × ${days})`}</dt><dd className="text-right">{formatPaise(delivery)}</dd>
          {discount > 0 && <><dt>Coupon discount</dt><dd className="text-right">−{formatPaise(discount)}</dd></>}
          <dt className="font-semibold">Total before tax</dt><dd className="text-right font-semibold">{formatPaise(Math.max(0, food + delivery - discount))}</dd>
        </dl>
        <p className="text-sm text-muted-foreground">Applicable tax is added at checkout; the final amount is confirmed before you pay.</p>
      </section>

      <section className="space-y-3 border-t border-border pt-6">
        <h2 className="font-display text-2xl">Delivery schedule</h2>
        <p className="text-sm">Delivery days: {s.working_days.map((d) => dayNames[d]).join(', ')}</p>
        {quote?.ok ? <><p className="text-sm">Starts {quote.start} · ends {quote.end} · {quote.deliveryDays} delivery {quote.deliveryDays === 1 ? 'day' : 'days'}</p>
          <p className="text-sm break-words">Dates: {quote.dates.join(', ')}</p></>
          : quote ? <p role="alert" className="text-sm text-destructive">{quote.reason}</p> : <p className="text-sm text-muted-foreground">Choose a start date and area to see your delivery dates.</p>}
        {(selection === 'both' ? (['lunch', 'dinner'] as const) : [selection]).map((m) => s[`${m}_delivery_time`] ? <p className="text-sm capitalize" key={m}>{m} delivery: {s[`${m}_delivery_time`]}</p> : null)}
        {s.terms && <p className="text-sm whitespace-pre-wrap">{s.terms}</p>}
      </section>

      {!session.user ? (
        <section className="space-y-3 border-t border-border pt-6">
          <h2 className="font-display text-2xl">Sign in to continue</h2>
          <p className="text-sm">Sign in to your account to enter delivery details and pay. Your choices will be kept.</p>
          <Button type="button" onClick={signIn}>Sign in to subscribe</Button>
        </section>
      ) : (<>
        <section className="space-y-4 border-t border-border pt-6">
          <h2 className="font-display text-2xl">Delivery details</h2>
          <SavedAddresses onSelect={useAddress} />
          <div className="grid gap-4 sm:grid-cols-2">
            {([
              { key: 'name', label: 'Full name', type: 'text', required: true },
              { key: 'phone', label: 'Mobile number', type: 'tel', required: true },
              { key: 'email', label: 'Email', type: 'email', required: false },
              { key: 'address', label: 'Delivery address', type: 'text', required: true },
              { key: 'line2', label: 'Address line 2 / landmark', type: 'text', required: false },
              { key: 'city', label: 'City', type: 'text', required: true },
              { key: 'pincode', label: 'PIN code', type: 'text', required: true },
            ] as const).map((f) => (
              <div key={f.key} className="space-y-2"><Label htmlFor={`tiffin-${f.key}`}>{f.label}</Label>
                <Input id={`tiffin-${f.key}`} type={f.type} required={f.required} value={details[f.key]}
                  pattern={f.key === 'pincode' ? '[1-9][0-9]{5}' : f.key === 'phone' ? '[+]?[0-9 ()-]{10,16}' : undefined}
                  onChange={(e) => setDetails((d) => ({ ...d, [f.key]: e.target.value }))} /></div>
            ))}
          </div>
          <Label htmlFor="tiffin-notes">Delivery instructions</Label>
          <Textarea id="tiffin-notes" value={details.notes} onChange={(e) => setDetails((d) => ({ ...d, notes: e.target.value }))} />
        </section>

        <section className="space-y-3 border-t border-border pt-6">
          <h2 className="font-display text-2xl">Coupon</h2>
          <div className="flex gap-2 max-w-md">
            <Input aria-label="Coupon code" placeholder="Coupon code" value={coupon} onChange={(e) => { setCoupon(e.target.value); setCouponState(null); }} />
            <Button type="button" variant="outline" disabled={!coupon.trim() || couponMutation.isPending} onClick={() => couponMutation.mutate()}>Apply</Button>
          </div>
          {couponState && 'error' in couponState && <p role="alert" className="text-sm text-destructive">{couponState.error}</p>}
          {couponState && 'code' in couponState && <p className="text-sm">{couponState.code} applied. Final eligibility for tiffin is confirmed when you place the order.</p>}
        </section>

        <section className="space-y-3 border-t border-border pt-6">
          <h2 className="font-display text-2xl">Payment</h2>
          <label className="flex items-center gap-2 text-sm"><input type="radio" name="tiffin-pay" checked={payment === 'razorpay'} onChange={() => setPayment('razorpay')} /> Pay online (Razorpay)</label>
          <label className="flex items-center gap-2 text-sm"><input type="radio" name="tiffin-pay" checked={payment === 'cod'} onChange={() => setPayment('cod')} /> Cash on delivery</label>
          {payment === 'cod' && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={codAck} onChange={(e) => setCodAck(e.target.checked)} /> I will pay the full amount in cash on the first delivery.</label>}
          <Button type="submit" disabled={!canSubmit}>{order.isPending ? 'Placing order…' : payment === 'razorpay' ? 'Continue to payment' : 'Place order'}</Button>
          {order.data && !order.data.ok && <p role="alert" className="text-sm text-destructive">{order.data.message}</p>}
          {order.isError && <p role="alert" className="text-sm text-destructive">Your tiffin order could not be placed. Please try again.</p>}
          <p className="text-xs text-muted-foreground">One-time payment for the plan you choose — no automatic renewal.</p>
        </section>
      </>)}
    </form>
  );
}

function SavedAddresses({ onSelect }: { onSelect: (a: CustomerAddress) => void }) {
  const { addresses, isLoading, error } = useCustomerAddresses();
  if (isLoading || error || !addresses.length) return null;
  return (
    <div className="space-y-2"><Label htmlFor="tiffin-address">Saved address</Label>
      <select id="tiffin-address" className={selectClass} defaultValue="" onChange={(e) => { const a = addresses.find((x) => x.id === e.target.value); if (a) onSelect(a); }}>
        <option value="">Select saved address</option>
        {addresses.map((a) => <option key={a.id} value={a.id}>{a.label} · {a.address_line1}</option>)}
      </select></div>
  );
}

/** Crawlable summary built only from configured settings and the live delivery-area list. */
function TiffinInfo({ catalog }: { catalog: Catalog }) {
  const s = catalog.settings;
  const { data: areas } = useSuspenseQuery(areasQuery);
  if (!s) return null;
  const serviceAreas = tiffinServiceAreas({ ...catalog, areas: areas ?? [] });
  const opts = durationOptions(s).filter((o) => o.enabled);
  const days = s.working_days.map((d) => dayNames[d]).join(', ');
  return (
    <section className="space-y-4 border-t border-border pt-8 text-sm leading-relaxed">
      <h2 className="font-display text-2xl">Vegetarian tiffin service in Mumbai</h2>
      <p>Poonam Kapur Healthy Kitchen delivers vegetarian lunch and dinner tiffins on {days}. Choose lunch only, dinner only, or lunch and dinner every delivery day.</p>
      <h3 className="font-semibold text-base">Plans and prices</h3>
      <ul className="list-disc pl-5">
        {s.lunch_price_paise != null && <li>Lunch: {formatPaise(s.lunch_price_paise)} per delivery</li>}
        {s.dinner_price_paise != null && <li>Dinner: {formatPaise(s.dinner_price_paise)} per delivery</li>}
        {s.delivery_policy === 'per_meal' && <li>Delivery: {formatPaise(s.delivery_price_paise)} per selected meal per delivery day</li>}
        {opts.map((o) => <li key={o.id}>{o.label}: {o.delivery_days} delivery {o.delivery_days === 1 ? 'day' : 'days'}</li>)}
      </ul>
      <p>Prices are before tax. Each plan is a one-time purchase with no automatic renewal.</p>
      <h3 className="font-semibold text-base">How to order</h3>
      <p>Pick your meals, plan and start date, then sign in to your account to add your delivery address and pay online or choose cash on delivery.</p>
      {!!serviceAreas.length && <><h3 className="font-semibold text-base">Delivery areas</h3><p>{serviceAreas.map((a) => a.neighbourhood).join(', ')}.</p></>}
      <p>Questions? <Link to="/contact" className="text-primary underline">Contact us</Link> or see our <Link to="/meal-plans" className="text-primary underline">meal plans</Link> and <Link to="/weekly-menu" className="text-primary underline">weekly menu</Link>.</p>
    </section>
  );
}
